import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  CheckCircleIcon, 
  SparklesIcon,
  FireIcon,
  TrophyIcon
} from '@heroicons/react/24/solid';
import { selectProgress, selectProgressMap } from '@/store/slices/interviewSlice';

const ProgressBar = () => {
  const progress = useSelector(selectProgress);
  const progressMap = useSelector(selectProgressMap);
  
  const [previousPercent, setPreviousPercent] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationMilestone, setCelebrationMilestone] = useState(null);

  if (!progress) return null;

  // 🔥 FIXED: Use correct API property names
  const questionsAnswered = progress.answered_questions || 0;
  const totalQuestions = progress.total_questions || 0;
  const percentComplete = progress.completion_percentage || 0; // completion_percentage not completion_percent
  const questionsRemaining = totalQuestions - questionsAnswered;

  // Milestones at 25%, 50%, 75%, 100%
  const milestones = [
    { percent: 25, label: '¼', icon: SparklesIcon, color: 'text-yellow-500', bg: 'bg-yellow-100' },
    { percent: 50, label: '½', icon: FireIcon, color: 'text-orange-500', bg: 'bg-orange-100' },
    { percent: 75, label: '¾', icon: TrophyIcon, color: 'text-purple-500', bg: 'bg-purple-100' },
    { percent: 100, label: '✓', icon: CheckCircleIcon, color: 'text-green-500', bg: 'bg-green-100' }
  ];

  // Detect milestone crossing and trigger celebration
  useEffect(() => {
    if (percentComplete > previousPercent) {
      const crossedMilestone = milestones.find(
        m => previousPercent < m.percent && percentComplete >= m.percent
      );

      if (crossedMilestone) {
        setCelebrationMilestone(crossedMilestone);
        setShowCelebration(true);
        
        setTimeout(() => {
          setShowCelebration(false);
        }, 2000);
      }
    }
    setPreviousPercent(percentComplete);
  }, [percentComplete]);

  // Get current streak status
  const getStreakStatus = () => {
    if (percentComplete >= 75) return { text: 'Almost there!', color: 'text-purple-600', icon: TrophyIcon };
    if (percentComplete >= 50) return { text: 'Halfway!', color: 'text-orange-600', icon: FireIcon };
    if (percentComplete >= 25) return { text: 'Great start!', color: 'text-yellow-600', icon: SparklesIcon };
    return { text: 'Just starting', color: 'text-indigo-600', icon: SparklesIcon };
  };

  const streak = getStreakStatus();
  const StreakIcon = streak.icon;

  return (
    <div className="relative">
      {/* Celebration overlay */}
      {showCelebration && celebrationMilestone && (
        <div className="absolute inset-0 -top-12 flex items-center justify-center pointer-events-none z-50 animate-celebration">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border-4 border-current animate-bounce-in">
            <div className={`flex items-center space-x-3 ${celebrationMilestone.color}`}>
              <celebrationMilestone.icon className="w-12 h-12" />
              <div>
                <div className="text-2xl font-bold">{celebrationMilestone.percent}% Complete!</div>
                <div className="text-sm opacity-75">Keep going! 🎉</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <StreakIcon className={`w-5 h-5 ${streak.color}`} />
            <span className={`text-sm font-semibold ${streak.color}`}>
              {streak.text}
            </span>
          </div>
          
          <div className="text-right">
            <div className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              {percentComplete.toFixed(0)}%
            </div>
            <div className="text-xs text-gray-500">Complete</div>
          </div>
        </div>

        {/* Progress bar with milestones */}
        <div className="relative mb-3">
          {/* Background track */}
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden relative">
            {/* Gradient fill */}
            <div 
              className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full transition-all duration-700 ease-out relative"
              style={{ width: `${percentComplete}%` }}
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
            </div>
          </div>

          {/* Milestone markers */}
          <div className="absolute inset-0 flex items-center">
            {milestones.map((milestone, index) => {
              const isReached = percentComplete >= milestone.percent;
              const position = milestone.percent;
              
              return (
                <div
                  key={index}
                  className="absolute transform -translate-x-1/2"
                  style={{ left: `${position}%` }}
                >
                  <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                    isReached 
                      ? `${milestone.bg} border-white shadow-lg scale-110` 
                      : 'bg-white border-gray-300 scale-100'
                  }`}>
                    {isReached ? (
                      <milestone.icon className={`w-4 h-4 ${milestone.color}`} />
                    ) : (
                      <span className="text-xs text-gray-400 font-medium">{milestone.label}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div>
              <span className="font-semibold text-gray-900">{questionsAnswered}</span>
              <span className="text-gray-500"> / {totalQuestions}</span>
              <span className="text-gray-400 ml-1">answered</span>
            </div>
            
            {questionsRemaining > 0 && (
              <div className="hidden sm:block">
                <span className="text-gray-500">{questionsRemaining} remaining</span>
              </div>
            )}
          </div>

          {/* Momentum indicator */}
          {questionsAnswered > 0 && (
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
              <span>In progress</span>
            </div>
          )}
        </div>

        {/* Stepper dots (optional) */}
        {progressMap && Object.keys(progressMap).length > 0 && (
          <div className="flex justify-center mt-4 space-x-2 pt-3 border-t border-gray-200">
            {Object.entries(progressMap).map(([position, status]) => (
              <div 
                key={position}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  status === 'done' ? 'bg-green-500' :
                  status === 'current' ? 'bg-indigo-600 ring-2 ring-indigo-300 scale-110' :
                  'bg-gray-300'
                }`}
                title={`Question ${position}: ${status}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Animations */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        @keyframes celebration {
          0% { 
            opacity: 0; 
            transform: scale(0.5) translateY(20px);
          }
          50% { 
            opacity: 1; 
            transform: scale(1.1) translateY(-10px);
          }
          100% { 
            opacity: 0; 
            transform: scale(0.9) translateY(-20px);
          }
        }
        
        .animate-celebration {
          animation: celebration 2s ease-out;
        }
        
        @keyframes bounce-in {
          0% { 
            transform: scale(0.3);
            opacity: 0;
          }
          50% { 
            transform: scale(1.05);
          }
          70% { 
            transform: scale(0.9);
          }
          100% { 
            transform: scale(1);
            opacity: 1;
          }
        }
        
        .animate-bounce-in {
          animation: bounce-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ProgressBar;